/** @velyn-custom BEGIN — vely-swiper-pagination.js (reusable pill pagination for Swiper) */
(function () {
  const SELECTOR = ".vely-swiper-pagination-pill";
  const BULLET_SELECTOR = ".swiper-pagination-bullet, .bullet";

  function resolveElement(rootOrEl) {
    if (!rootOrEl) return null;
    if (typeof rootOrEl === "string") {
      return document.querySelector(rootOrEl);
    }
    if (rootOrEl.matches?.(SELECTOR)) {
      return rootOrEl;
    }
    return rootOrEl.querySelector?.(SELECTOR) || null;
  }

  function pinInFlow(el) {
    if (!el || !(el instanceof Element)) return;

    const anchor = el.closest("[data-vely-pagination-anchor]");
    if (anchor && el.parentElement !== anchor) {
      anchor.appendChild(el);
    }

    el.classList.remove(
      "swiper-pagination-hidden",
      "swiper-pagination-disabled",
      "swiper-pagination-lock"
    );
    el.style.setProperty("position", "relative", "important");
    el.style.setProperty("inset", "auto", "important");
    el.style.setProperty("top", "auto", "important");
    el.style.setProperty("bottom", "auto", "important");
    el.style.setProperty("left", "auto", "important");
    el.style.setProperty("right", "auto", "important");
    el.style.setProperty("transform", "none", "important");
    el.style.setProperty("width", "fit-content", "important");
    el.style.setProperty("opacity", "1", "important");
    el.style.setProperty("visibility", "visible", "important");
    el.style.setProperty("display", "flex", "important");
    el.style.setProperty("flex-direction", "row", "important");
  }

  function countSlides(swiper) {
    if (!swiper) return 0;

    // DOM is more reliable than swiper.slides on the first paint / while
    // observer is still catching up (common cause of a stuck 1-bullet track).
    const fromDom = swiper.el
      ? swiper.el.querySelectorAll(
          ".swiper-wrapper > .swiper-slide:not(.swiper-slide-duplicate)"
        ).length
      : 0;

    let fromApi = 0;
    if (swiper.slides?.length) {
      if (swiper.params?.loop) {
        fromApi = swiper.slides.filter(
          (slide) => !slide.classList.contains("swiper-slide-duplicate")
        ).length;
      } else {
        fromApi = swiper.slides.length;
      }
    }

    return Math.max(fromDom, fromApi);
  }

  function getSlidesPerViewNumber(swiper) {
    const spv = swiper?.params?.slidesPerView ?? 1;
    if (typeof spv === "number" && Number.isFinite(spv)) return spv;
    if (typeof spv === "string" && spv !== "auto") {
      const parsed = parseFloat(spv);
      if (Number.isFinite(parsed)) return parsed;
    }
    return 1;
  }

  function countByFormula(slideCount, spv) {
    if (slideCount <= 1) return slideCount;
    if (spv >= slideCount) return 1;
    return Math.max(1, slideCount - Math.floor(spv) + 1);
  }

  function countPaginationItems(swiper) {
    if (!swiper) return 0;

    // Settle sizes before counting — first init often has empty/stale slides
    // and an undercounted snapGrid (locks the pill track at 1 bullet).
    swiper.updateSize?.();
    swiper.updateSlides?.();
    swiper.updateProgress?.();
    swiper.update?.();

    const slideCount = countSlides(swiper);
    if (slideCount <= 1) return slideCount;

    const spv = getSlidesPerViewNumber(swiper);
    const byFormula = countByFormula(slideCount, spv);
    const bySnap = swiper.snapGrid?.length || 0;

    // Trust snapGrid only when settled and not an undercount.
    // Do NOT trust a 1:1 snap-per-slide grid when slidesPerView > 1 —
    // that overcounts pages and leaves empty trailing space on the last snaps
    // (recently-viewed / multi-card carousels).
    if (bySnap > 1 && bySnap >= byFormula) {
      if (spv > 1.01 && bySnap >= slideCount && byFormula < bySnap) {
        return byFormula;
      }
      return bySnap;
    }

    return byFormula;
  }

  function hasBullets(el) {
    return Boolean(el?.querySelector(BULLET_SELECTOR));
  }

  function getSlideLabelTemplate(el) {
    if (!el) return "";
    return (
      el.dataset.slideLabel ||
      el.closest("[data-slide-label]")?.dataset.slideLabel ||
      ""
    );
  }

  function formatSlideLabel(template, oneBasedIndex) {
    if (!template) return "";
    return template
      .replace(/__NUMBER__/g, String(oneBasedIndex))
      .replace(/\{\{\s*number\s*\}\}/gi, String(oneBasedIndex));
  }

  function goToSlide(swiper, index) {
    if (!swiper) return;
    const group = Number(swiper.params?.slidesPerGroup) || 1;
    const slideIndex = index * group;
    if (swiper.params?.loop) {
      swiper.slideToLoop(slideIndex);
      return;
    }
    swiper.slideTo(slideIndex);
  }

  function buildBullets(swiper, el, countOverride) {
    const slideCount =
      typeof countOverride === "number" && countOverride >= 0
        ? countOverride
        : countPaginationItems(swiper);
    const labelTemplate = getSlideLabelTemplate(el);
    el.innerHTML = "";

    for (let i = 0; i < slideCount; i += 1) {
      const bullet = document.createElement("span");
      bullet.className = "swiper-pagination-bullet bullet";
      bullet.setAttribute("role", "tab");
      bullet.setAttribute(
        "aria-label",
        formatSlideLabel(labelTemplate, i + 1),
      );
      bullet.addEventListener("click", () => goToSlide(swiper, i));
      el.appendChild(bullet);
    }
  }

  function getActiveBulletIndex(swiper, bulletCount) {
    if (!swiper) return 0;

    const total =
      typeof bulletCount === "number" && bulletCount > 0
        ? bulletCount
        : 0;

    // At track end (incl. slidesOffsetAfter / pinned last snap), activeIndex
    // often stays on an earlier slide while the last slide is visible on the right.
    if (swiper.isEnd || (swiper.progress ?? 0) >= 0.99) {
      if (total > 0) return total - 1;
    }

    if (swiper.params?.loop) return swiper.realIndex ?? 0;

    // Page-based carousels: snapIndex maps 1:1 to bullets when counts match.
    if (
      total > 0 &&
      typeof swiper.snapIndex === "number" &&
      swiper.snapGrid?.length === total
    ) {
      return Math.max(0, Math.min(swiper.snapIndex, total - 1));
    }

    const idx = swiper.activeIndex ?? swiper.snapIndex ?? 0;
    if (total > 0) return Math.max(0, Math.min(idx, total - 1));
    return idx;
  }

  /**
   * Keep a moving window of full-size bullets around the active one.
   * Enabled via data-vely-size-window="N" on the pagination element.
   */
  function applySizeWindow(el, activeIndex) {
    if (!el) return;

    const windowSize = Number(el.dataset.velySizeWindow) || 0;
    const bullets = el.querySelectorAll(BULLET_SELECTOR);
    const total = bullets.length;

    if (windowSize < 1 || total === 0) {
      bullets.forEach((bullet) => {
        bullet.classList.remove("vely-pagination-bullet--sm");
      });
      return;
    }

    if (total <= windowSize) {
      bullets.forEach((bullet) => {
        bullet.classList.remove("vely-pagination-bullet--sm");
      });
      return;
    }

    const safeActive = Math.max(0, Math.min(activeIndex, total - 1));
    // Prefer ~1 neighbor before active when possible (active + 3 nearby = 4).
    let start = safeActive - Math.floor((windowSize - 1) / 2);
    start = Math.max(0, Math.min(start, total - windowSize));
    const end = start + windowSize;

    bullets.forEach((bullet, index) => {
      const isOutside = index < start || index >= end;
      bullet.classList.toggle("vely-pagination-bullet--sm", isOutside);
    });
  }

  function setActiveBullet(swiper, elOverride) {
    const el = elOverride || swiper?.pagination?.el;
    if (!el || !swiper) return;

    const bullets = el.querySelectorAll(BULLET_SELECTOR);
    const activeIndex = getActiveBulletIndex(swiper, bullets.length);

    bullets.forEach((bullet, index) => {
      bullet.classList.toggle(
        "swiper-pagination-bullet-active",
        index === activeIndex
      );
    });

    applySizeWindow(el, activeIndex);
  }

  function refresh(swiper, elOverride, countOverride) {
    const el = elOverride || swiper?.pagination?.el;
    if (!el || !swiper) return;

    pinInFlow(el);
    swiper.updateSize?.();
    swiper.updateSlides?.();
    swiper.updateProgress?.();
    swiper.update?.();

    const pageCount =
      typeof countOverride === "number" && countOverride >= 0
        ? countOverride
        : countPaginationItems(swiper);
    const bulletCount = el.querySelectorAll(BULLET_SELECTOR).length;

    if (pageCount > 0 && bulletCount !== pageCount) {
      buildBullets(swiper, el, pageCount);
    } else if (!hasBullets(el) && pageCount > 0) {
      buildBullets(swiper, el, pageCount);
    } else if (pageCount === 0) {
      el.innerHTML = "";
    }

    setActiveBullet(swiper, el);
  }

  /**
   * Always wipe + rebuild to an explicit count (or recomputed page count).
   * Use for sections where snapGrid heuristics stay stuck (mobile peek).
   */
  function forceRebuild(swiper, elOverride, countOverride) {
    const el = elOverride || swiper?.pagination?.el;
    if (!el || !swiper) return;
    pinInFlow(el);
    const pageCount =
      typeof countOverride === "number" && countOverride >= 0
        ? countOverride
        : countPaginationItems(swiper);
    buildBullets(swiper, el, pageCount);
    setActiveBullet(swiper, el);
  }

  function shouldShowPagination(swiper) {
    return countPaginationItems(swiper) > 1;
  }

  window.VelySwiperPagination = {
    SELECTOR,
    BULLET_CLASS: "swiper-pagination-bullet bullet",
    BULLET_ACTIVE_CLASS: "swiper-pagination-bullet-active",
    pinInFlow,
    refresh,
    forceRebuild,
    setActiveBullet,
    shouldShowPagination,
    getPageCount: countPaginationItems,

    /**
     * Returns a Swiper pagination config object, or false if element missing.
     * @param {HTMLElement|string} rootOrEl — host section or pagination element / selector
     */
    getConfig(rootOrEl) {
      const el = resolveElement(rootOrEl);
      if (!el) return false;
      const labelTemplate = getSlideLabelTemplate(el);
      return {
        clickable: true,
        type: "bullets",
        el,
        bulletClass: this.BULLET_CLASS,
        bulletActiveClass: this.BULLET_ACTIVE_CLASS,
        renderBullet(index, className) {
          const ariaLabel = formatSlideLabel(labelTemplate, index + 1).replace(
            /"/g,
            "&quot;",
          );
          return `<span class="${className} bullet" role="tab" aria-label="${ariaLabel}"></span>`;
        },
      };
    },
  };
})();
/** @velyn-custom END — vely-swiper-pagination.js */
